import React, { ComponentType, ForwardRefRenderFunction, useRef } from 'react'
import { isForwardRef } from 'react-is'
import { named, withDependencies } from '@wix/thunderbolt-ioc'
import {
	LayoutChangeHookSymbol,
	RenderDoneHookSymbol,
	LayoutHook,
	CompRef,
	EditorFeatureConfigSymbol,
} from '@wix/thunderbolt-symbols'
import {
	IComponentScrollApi,
	IMeasuresApi,
	InnerScrollApiSymbol,
	InnerScrollApiType,
} from '@wix/thunderbolt-becky-types'
import type { IWrapComponent } from '@wix/thunderbolt-components-loader'
import { RegisterComponentMeasuresAPI, RegisterComponentsMeasureSymbol } from 'ds-feature-components-measures'
import AutoHeightWrapper from './AutoHeightWrapper'
import { name } from '../symbols'
import type { ViewerManagerLayoutApiEditorConfig } from '../types'
import { MeasuresApiSymbol } from 'ds-feature-measures-api'

type IComponentWithInnerScrollImperativeAPI = {
	scrollApi: IComponentScrollApi
}

type ComponentWithInnerScrollRef = CompRef & IComponentWithInnerScrollImperativeAPI

const isComponentScrollable = (ref: CompRef): ref is ComponentWithInnerScrollRef => !!ref && 'scrollApi' in ref

export const LayoutHOC = withDependencies(
	[
		named(EditorFeatureConfigSymbol, name),
		InnerScrollApiSymbol,
		RenderDoneHookSymbol,
		LayoutChangeHookSymbol,
		RegisterComponentsMeasureSymbol,
		MeasuresApiSymbol,
	],
	(
		config: ViewerManagerLayoutApiEditorConfig,
		innerScrollApi: InnerScrollApiType,
		renderDoneHook: LayoutHook,
		layoutChangeHook: LayoutHook,
		registerComponentMeasuresAPI: RegisterComponentMeasuresAPI,
		measuresApi: IMeasuresApi
	): IWrapComponent => {
		return {
			wrapComponent: <T extends { id: string }>(Component: ComponentType<T>) => {
				const Wrapper: ForwardRefRenderFunction<any, T> = (props: T, ref) => {
					const { id } = props
					const unregisterScroll = useRef<(() => void) | null>(null)
					layoutChangeHook.useObserver(id)

					const registerRef = (passedRef: CompRef) => {
						// Passing the ref to the parent ref
						if (ref && typeof ref === 'function') {
							ref(passedRef)
						}
						// Registering the custom measures with the passed ref
						registerComponentMeasuresAPI.registerComponentCustomMeasures(id, passedRef)

						if (isComponentScrollable(passedRef)) {
							unregisterScroll.current = innerScrollApi.registerToInnerScroll(id, passedRef.scrollApi)
						}
						if (passedRef === null && unregisterScroll.current) {
							unregisterScroll.current()
							unregisterScroll.current = null
						}
					}
					const compWithProps = <Component {...props} />
					const compWithPropsAndForwardRef = isForwardRef(compWithProps) ? (
						<Component {...props} ref={registerRef} />
					) : (
						compWithProps
					)
					return measuresApi.isAutoHeightComp(id) ? (
						<AutoHeightWrapper id={measuresApi.getCompIdSelectorForMesh(id)} contentId={id}>
							{compWithPropsAndForwardRef}
						</AutoHeightWrapper>
					) : (
						compWithPropsAndForwardRef
					)
				}
				const MemoWrapper = React.memo(React.forwardRef(Wrapper))

				return React.forwardRef((props: any, ref: any) => {
					renderDoneHook.useObserver(props.id)
					return <MemoWrapper {...props} ref={ref} />
				})
			},
		}
	}
)
